// src/PageInvite.js
import { useState, useRef } from 'react';
import { doc, getDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';

const DIAPORAMA_ID = 'wGkKHFfvUaEbDjM3Sxw0';
const NOM_COLLECTION = '50iemeFrancineYvon';

const TAILLE_MAX_PHOTO = 5 * 1024 * 1024;   // 5 Mo
const TAILLE_MAX_VIDEO = 50 * 1024 * 1024; // 100 Mo

function PageInvite() {
  const [codeEntre, setCodeEntre] = useState('');
  const [accesAutorise, setAccesAutorise] = useState(false);
  const [erreur, setErreur] = useState('');
  const [chargement, setChargement] = useState(false);
  const [televersementActif, setTeleversementActif] = useState(true);
  const inputFichierRef = useRef(null);
  const refInputFichier = useRef(null);
  const [nomFichierEnvoye, setNomFichierEnvoye] = useState('');

  // Champs du formulaire d'upload
  const [nomAuteur, setNomAuteur] = useState('');
  const [message, setMessage] = useState('');
  const [fichier, setFichier] = useState(null);
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const [confirmationEnvoi, setConfirmationEnvoi] = useState(false);

  const verifierCode = async () => {
    setErreur('');
    setChargement(true);

    try {
      const refDocument = doc(db, NOM_COLLECTION, DIAPORAMA_ID);
      const snapshot = await getDoc(refDocument);

      if (!snapshot.exists()) {
        setErreur('Diaporama introuvable.');
        setChargement(false);
        return;
      }

      const donnees = snapshot.data();

      if (donnees.codeAcces === codeEntre.trim()) {
        setAccesAutorise(true);
        setTeleversementActif(donnees.televersementActif);
      } else {
        setErreur('Code d\'accès incorrect.');
      }
    } catch (err) {
      console.error(err);
      setErreur('Une erreur est survenue. Réessaie.');
    }

    setChargement(false);
  };

  const envoyerFichier = async () => {
    if (!fichier) {
      setErreur('Choisis une photo ou une vidéo.');
      return;
    }
    if (!nomAuteur.trim()) {
      setErreur('Entre ton nom.');
      return;
    }

    // Déterminer le type selon le fichier
    const type = fichier.type.startsWith('video') ? 'video' : 'photo';

    // ✅ NOUVELLE VALIDATION — taille du fichier
    const limiteApplicable = type === 'video' ? TAILLE_MAX_VIDEO : TAILLE_MAX_PHOTO;
    if (fichier.size > limiteApplicable) {
      const limiteMo = type === 'video' ? '50 Mo' : '5 Mo';
      setErreur(`Le fichier est trop volumineux. Limite : ${limiteMo}.`);
      return;
    }

    setErreur('');
    setEnvoiEnCours(true);

    try {
      // 2. Téléverser le fichier dans Firebase Storage
      const nomFichierUnique = `${Date.now()}_${fichier.name}`;
      const refFichier = ref(storage, `medias/${DIAPORAMA_ID}/${nomFichierUnique}`);
      await uploadBytes(refFichier, fichier);

      // 3. Récupérer l'URL publique du fichier téléversé
      const url = await getDownloadURL(refFichier);

      // 4. Créer le document dans la collection "medias"
      await addDoc(collection(db, 'medias'), {
        diaporamaId: DIAPORAMA_ID,
        type: type,
        url: url,
        dateAjout: serverTimestamp(),
        nomAuteur: nomAuteur.trim(),
        message: message.trim().slice(0, 100)
      });

      // 5. Réinitialiser le formulaire
      setNomFichierEnvoye(fichier.name); // ← sauvegarde le nom avant de vider "fichier"
      setFichier(null);
      setMessage('');
      setConfirmationEnvoi(true);
      if (inputFichierRef.current) {
        inputFichierRef.current.value = '';
      }
      if (refInputFichier.current) {
        refInputFichier.current.value = '';
      }
    } catch (err) {
      console.error(err);
      setErreur('Erreur lors de l\'envoi. Réessaie.');
    }

    setEnvoiEnCours(false);
  };

  if (accesAutorise) {
    if (!televersementActif) {
      return <p>Le téléversement est actuellement désactivé par l'organisateur.</p>;
    }

    return (
      <div>
        <h2 className='page-invite'>Ajouter une photo ou vidéo</h2>

        <div className="champ-formulaire page-invite">
          <label >Votre nom (Obligatoire)</label>
          <input
            type="text"
            value={nomAuteur}
            onChange={(e) => setNomAuteur(e.target.value)}
            maxLength={50}
            placeholder="Votre nom"
          />
        </div>

        <div className="champ-formulaire page-invite">
          <label>Message (Optionnel, max 100 caractères) - {message.length} caractère(s) : </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={100}
            placeholder="Un petit mot..."
          />
        </div>

        <div className="champ-formulaire page-invite">
          <input
            className='page-invite'
            ref={inputFichierRef}
            type="file"
            accept="image/*,video/*"
            onChange={(e) => {
              setFichier(e.target.files[0]);
              setConfirmationEnvoi(false); // efface le message de succès si présent
            }}
          />
          <p style={{ fontSize: '0.8em', color: 'gray' }}>
            Photos : 5 Mo max. Vidéos : 50 Mo max.
          </p>
        </div>

        <button className='page-invite' onClick={envoyerFichier} disabled={envoiEnCours || !fichier}>
          {envoiEnCours ? 'Envoi en cours...' : 'Envoyer'}
        </button>

        {erreur && <p style={{ color: 'red' }}>{erreur}</p>}
        {confirmationEnvoi && <p style={{ color: 'green' }}>✅ "{nomFichierEnvoye}" Envoyé avec succès ! Tu peux en ajouter d'autre en choissisant un nouveau fichier!</p>}
      </div>
    );
  }

  return (
    <div>
      <h2 className="page-invite">Entrez le code d'accès</h2>
      <input
        className="page-invite"
        type="text"
        value={codeEntre}
        onChange={(e) => setCodeEntre(e.target.value.toUpperCase())}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            verifierCode();
          }
        }}
        placeholder="Code d'accès"
      />
      <button onClick={verifierCode} disabled={chargement}>
        {chargement ? 'Vérification...' : 'Valider'}
      </button>
      {erreur && <p style={{ color: 'red' }}>{erreur}</p>}
    </div>
  );
}

export default PageInvite;